/**
 * Cart drawer.
 *
 * Hand-written and unbundled: assets/layout.theme.js.liquid and the template.*.js chunks
 * are compiled webpack output with no source in this repo. See README, "Assets & the
 * missing build step".
 *
 * Vanilla on purpose. jQuery is a global here but it comes from code.jquery.com, and the
 * cart is too important to gate on a third-party CDN.
 *
 * Loads on every page (except app-proxy paths) even when the drawer is disabled, because
 * it also owns the WINEM8 discount auto-apply that snippets/cart-discount-notice.liquid
 * used to do with an inline <script>.
 */
(function () {
	'use strict';

	var cfg = (window.theme && window.theme.cartDrawer) || {};
	var CART_URL = cfg.cartUrl || '/cart';
	var strings = cfg.strings || {};

	/* ------------------------------------------------------------------ helpers */

	function closest(target, selector) {
		if (!target || typeof target.closest !== 'function') return null;
		return target.closest(selector);
	}

	function onAppProxyPath() {
		return !!cfg.appPath && window.location.pathname.indexOf(cfg.appPath) !== -1;
	}

	/**
	 * Always reach through window. Klaviyo monkey-patches window.fetch inside a
	 * window 'load' handler — i.e. after this deferred script has run — so caching a
	 * reference here would make cart adds invisible to its tracking.
	 */
	function request(url, body) {
		return window
			.fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify(body)
			})
			.then(function (res) {
				return res.json().then(
					function (data) {
						return { ok: res.ok, status: res.status, data: data };
					},
					function () {
						// A 5xx can return HTML rather than JSON.
						throw new Error('Bad response from ' + url);
					}
				);
			});
	}

	/* --------------------------------------------------- WINEM8 discount codes */

	var appliedDiscounts = {};

	/**
	 * Applies the member discount code the server told us about. Fires once per code per
	 * page load, matching the behaviour of the inline script this replaced (not once per
	 * drawer refresh). Fire-and-forget, exactly as before.
	 */
	function applyDiscounts(root) {
		var nodes = (root || document).querySelectorAll('[data-cart-discount]');
		for (var i = 0; i < nodes.length; i++) {
			var code = nodes[i].getAttribute('data-cart-discount');
			if (!code || appliedDiscounts[code]) continue;
			appliedDiscounts[code] = true;
			try {
				window.fetch('/discount/' + encodeURIComponent(code));
			} catch (e) {
				/* matches the original try/catch-and-forget */
			}
		}
	}

	/* ------------------------------------------------------------- drawer setup */

	var drawer = document.querySelector('[data-cart-drawer]');
	var drawerActive = !!(cfg.enabled && drawer && !onAppProxyPath());

	/**
	 * Reads the variant and quantity a product card wants to add.
	 *
	 * Mirrors what the bundle used to do, but scopes the quantity lookup to the card
	 * first: template.collection.js uses an unscoped
	 * document.querySelector('.quantity-selector__input--' + id) where first match wins,
	 * so when the same wine appears in a grid and in recommended-products the two can
	 * disagree.
	 */
	function readCardAdd(button) {
		var variantId = button.getAttribute('data-variant-id');
		if (!variantId) return null;

		var card = closest(button, '.product-card');
		var input =
			(card && card.querySelector('.quantity-selector__input')) ||
			document.querySelector('.quantity-selector__input--' + variantId);

		var quantity = input ? parseInt(input.value, 10) : 1;
		// The bundle's steppers clamp to 0, so a card can otherwise post quantity 0.
		if (!quantity || quantity < 1) quantity = 1;

		return { id: variantId, quantity: quantity };
	}

	/**
	 * Pre-drawer behaviour: add, then hard-navigate to the cart page.
	 *
	 * Needed because renaming data-add-to-cart de-registered the bundle's own card
	 * handlers, so this file is now the only thing driving those buttons. Without this,
	 * turning the drawer off — or simply being on a page that does not render it, such as
	 * /cart — would leave every card's Add to cart button dead.
	 */
	function legacyCardAdd(body) {
		request('/cart/add.js', body)
			.then(function () {
				window.location = CART_URL;
			})
			.catch(function () {
				window.location = CART_URL;
			});
	}

	/**
	 * Registered before the drawer wiring below, so it survives the early return when no
	 * drawer is present on this page.
	 *
	 * Delegated on document because collection grids are injected by
	 * template.collection.js after load and re-injected on every filter and sort, so the
	 * buttons do not exist at DOMContentLoaded and are destroyed and recreated repeatedly.
	 */
	document.addEventListener(
		'click',
		function (event) {
			var button = closest(event.target, '[data-cart-drawer-add]');
			if (!button || button.disabled) return;

			event.preventDefault();
			if (button.getAttribute('aria-busy') === 'true') return;

			var body = readCardAdd(button);
			if (!body) return;

			if (drawerActive) {
				addToCart(body, button, 'product-card');
			} else {
				button.setAttribute('aria-busy', 'true');
				legacyCardAdd(body);
			}
		},
		false
	);

	// The discount auto-apply and the card fallback above must work everywhere; the rest
	// of the drawer wiring only applies where a drawer is actually rendered.
	if (!drawerActive) {
		applyDiscounts();
		return;
	}

	var panel = drawer.querySelector('[data-cart-drawer-panel]');
	var live = document.querySelector('[data-cart-live]');
	var opener = null;
	var isOpen = false;
	var pending = null;

	var FOCUSABLE =
		'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
		'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

	function announce(message) {
		if (live && message) live.textContent = message;
	}

	function setError(message) {
		var node = drawer.querySelector('[data-cart-error]');
		if (!node) return;
		node.textContent = message || '';
		if (message) {
			node.removeAttribute('hidden');
		} else {
			node.setAttribute('hidden', '');
		}
	}

	/* ------------------------------------------------------------- scroll lock */

	var lockCount = 0;

	function lockScroll() {
		lockCount += 1;
		if (lockCount === 1) document.documentElement.classList.add('cart-drawer-scroll-lock');
	}

	function unlockScroll() {
		lockCount = Math.max(0, lockCount - 1);
		if (lockCount === 0) document.documentElement.classList.remove('cart-drawer-scroll-lock');
	}

	/* ------------------------------------------------------------- open / close */

	function setTriggerState(expanded) {
		var triggers = document.querySelectorAll('[data-cart-drawer-open]');
		for (var i = 0; i < triggers.length; i++) {
			triggers[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
		}
	}

	function open(trigger) {
		if (isOpen) return;
		isOpen = true;
		if (trigger) opener = trigger;

		drawer.removeAttribute('hidden');
		drawer.setAttribute('aria-hidden', 'false');
		// Force a reflow so the transform transition actually runs from its start value.
		void drawer.offsetWidth;
		drawer.classList.add('is-open');
		setTriggerState(true);
		lockScroll();

		if (panel) {
			var close = panel.querySelector('[data-cart-drawer-close]');
			window.requestAnimationFrame(function () {
				(close || panel).focus();
			});
		}
	}

	function close() {
		if (!isOpen) return;
		isOpen = false;

		drawer.classList.remove('is-open');
		drawer.setAttribute('aria-hidden', 'true');
		setTriggerState(false);
		unlockScroll();
		setError('');

		var done = function () {
			if (!isOpen) drawer.setAttribute('hidden', '');
		};
		if (panel) {
			panel.addEventListener('transitionend', done, { once: true });
			// Belt and braces if the transition never fires (reduced motion, hidden tab).
			window.setTimeout(done, 500);
		} else {
			done();
		}

		// The opener may have been a product card that a collection filter has since
		// replaced via innerHTML, so it can be detached.
		if (opener && document.contains(opener)) {
			opener.focus();
		} else {
			var fallback = document.querySelector('[data-cart-drawer-open]');
			if (fallback) fallback.focus();
		}
		opener = null;
	}

	/* --------------------------------------------------------------- focus trap */

	function trapFocus(event) {
		if (event.key !== 'Tab' || !panel) return;

		// Re-queried every time: the drawer's contents are replaced wholesale on each
		// mutation, so a cached list would hold detached nodes and the trap would break.
		var nodes = [];
		var candidates = panel.querySelectorAll(FOCUSABLE);
		for (var i = 0; i < candidates.length; i++) {
			if (candidates[i].offsetParent !== null) nodes.push(candidates[i]);
		}
		if (!nodes.length) return;

		var first = nodes[0];
		var last = nodes[nodes.length - 1];

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}

	/**
	 * Puts focus back after a section swap.
	 *
	 * renderSection replaces the drawer's contents wholesale, which destroys whatever the
	 * customer was focused on — press + with the keyboard and focus would otherwise fall
	 * back to <body>, stranding them outside the dialog. `hint` is a selector for the
	 * equivalent control in the fresh markup; if the line is gone, fall back to Close.
	 */
	function refocusAfterSwap(hint) {
		var el = hint ? drawer.querySelector(hint) : null;
		if (el) {
			el.focus();
			return;
		}
		var closeBtn = panel && panel.querySelector('[data-cart-drawer-close]');
		if (closeBtn) closeBtn.focus();
	}

	/* ------------------------------------------------------- section rendering */

	/**
	 * GET /?sections=x returns a flat map; POST /cart/*.js with `sections` in the body
	 * nests it under `sections`. Handle both.
	 */
	function extractSection(payload) {
		var map = payload && payload.sections ? payload.sections : payload;
		var html = map && map['cart-drawer'];
		if (!html) throw new Error('cart-drawer section missing from response');

		var doc = new DOMParser().parseFromString(html, 'text/html');
		var fresh = doc.querySelector('[data-cart-drawer-contents]');
		var live_ = drawer.querySelector('[data-cart-drawer-contents]');
		if (!fresh || !live_) throw new Error('cart-drawer markup missing');

		return { fresh: fresh, target: live_, root: doc.querySelector('[data-cart-drawer]') };
	}

	function setCartCount(total) {
		if (total === null || total === undefined) return;
		var value = String(total);

		var bubbles = document.querySelectorAll('[data-cart-count]');
		for (var i = 0; i < bubbles.length; i++) {
			bubbles[i].textContent = value;
			if (value === '0') {
				bubbles[i].classList.add('cart-count--empty');
			} else {
				bubbles[i].classList.remove('cart-count--empty');
			}
		}

		var one = strings.itemsOne || 'item';
		var other = strings.itemsOther || 'items';
		var labels = document.querySelectorAll('[data-cart-count-label]');
		for (var j = 0; j < labels.length; j++) {
			labels[j].textContent = value + ' ' + (value === '1' ? one : other);
		}
	}

	function renderSection(payload) {
		var parts = extractSection(payload);
		parts.target.innerHTML = parts.fresh.innerHTML;

		if (parts.root) {
			var total = parts.root.getAttribute('data-cart-count-total');
			drawer.setAttribute('data-cart-count-total', total);
			setCartCount(total);
		}

		applyDiscounts(drawer);
	}

	/** Any failure to render server truth falls back to the cart page — today's behaviour. */
	function bail() {
		window.location = CART_URL;
	}

	function sectionParams() {
		return { sections: 'cart-drawer', sections_url: window.location.pathname };
	}

	/* ------------------------------------------------------------------ mutating */

	function setBusy(on) {
		if (on) {
			drawer.classList.add('is-busy');
			drawer.setAttribute('aria-busy', 'true');
		} else {
			drawer.classList.remove('is-busy');
			drawer.removeAttribute('aria-busy');
		}
	}

	/** Serialised rather than debounced: every response is authoritative server HTML. */
	function mutate(url, body) {
		var run = function () {
			setBusy(true);
			return request(url, Object.assign({}, body, sectionParams())).then(
				function (result) {
					setBusy(false);
					if (!result.ok) {
						var data = result.data || {};
						throw { handled: true, message: data.description || data.message || strings.error };
					}
					return result.data;
				},
				function (err) {
					setBusy(false);
					throw err;
				}
			);
		};

		pending = (pending || Promise.resolve()).then(run, run);
		return pending;
	}

	/* ---------------------------------------------------------------- add to cart */

	function addToCart(body, busyEl, source) {
		if (busyEl) busyEl.setAttribute('aria-busy', 'true');
		setError('');
		// Otherwise a failed add's message sits there contradicting the next successful one.
		clearAddError(busyEl);

		return mutate('/cart/add.js', body)
			.then(function (payload) {
				renderSection(payload);
				open(busyEl);
				announce(strings.added || '');
				// Only on success, and only after the drawer holds server truth. The gift
				// card recipient form listens for this to clear itself, and must not clear
				// on a failed add.
				document.dispatchEvent(
					new CustomEvent('cart:added', { detail: { source: source || null, id: body.id } })
				);
			})
			.catch(function (err) {
				if (err && err.handled) {
					showAddError(busyEl, err.message);
					announce(err.message);
				} else {
					bail();
				}
			})
			.then(function () {
				// Always clear the busy flag: leaving add-to-cart stuck is the worst
				// failure available here.
				if (busyEl) busyEl.removeAttribute('aria-busy');
			});
	}

	/** setError only reaches the drawer's own banner, so the per-form node needs its own. */
	function formErrorNode(el) {
		var scope = closest(el, '.product-card__product-form, form');
		return scope ? scope.querySelector('[data-cart-error]') : null;
	}

	/**
	 * Renders an add error next to the offending control. Never touches
	 * [data-submit-button].disabled or [data-submit-button-text]: the read-only bundle's
	 * renderSubmitButton owns both and rewrites them on every variant change.
	 */
	function showAddError(el, message) {
		var node = formErrorNode(el);
		if (node) {
			node.textContent = message;
			node.removeAttribute('hidden');
			return;
		}
		setError(message);
		open(el);
	}

	function clearAddError(el) {
		var node = formErrorNode(el);
		if (!node) return;
		node.textContent = '';
		node.setAttribute('hidden', '');
	}

	/* ------------------------------------------------------------------ listeners */

	/**
	 * PDP form. BUBBLE phase, deliberately: @shopify/theme-product-form binds its own
	 * submit listener on the form element (target phase) and its _onSubmit creates or
	 * updates the hidden [name="id"] input. A listener on document therefore always runs
	 * after it, so FormData already carries the correct variant.
	 */
	document.addEventListener(
		'submit',
		function (event) {
			if (event.defaultPrevented) return;

			var form = event.target;
			if (!form || form.tagName !== 'FORM') return;
			// Regex, not equality: a non-primary locale renders /en-au/cart/add.
			if (!/\/cart\/add(\?|$)/.test(form.getAttribute('action') || '')) return;

			var data = new FormData(form);
			var id = data.get('id');

			// Validate BEFORE preventDefault — it cannot be undone. _onSubmit throws a
			// TypeError when the chosen options map to no variant, and a throwing listener
			// does not stop propagation, so we can be reached with no usable id. Returning
			// here leaves the native POST to /cart intact, exactly as today.
			if (!id) return;

			event.preventDefault();

			var body = { id: id, quantity: parseInt(data.get('quantity'), 10) || 1 };

			var sellingPlan = data.get('selling_plan');
			if (sellingPlan) body.selling_plan = sellingPlan;

			var properties = {};
			var hasProperties = false;
			data.forEach(function (value, key) {
				var match = /^properties\[(.+)\]$/.exec(key);
				if (match && value !== '') {
					properties[match[1]] = value;
					hasProperties = true;
				}
			});
			if (hasProperties) body.properties = properties;

			addToCart(body, form.querySelector('[data-submit-button]') || form, 'product-form');
		},
		false
	);

	/**
	 * Header cart icon. CAPTURE phase, and this is not optional:
	 * assets/layout.theme.js.liquid module 128 binds a click handler directly to every
	 * same-host <a> at DOMContentLoaded, calls preventDefault, and navigates on the
	 * #transition animationend. A bubble listener would run after the fade had started.
	 * stopPropagation during capture keeps the event from ever reaching that handler,
	 * while still letting other document-level listeners (GTM) see it.
	 *
	 * (Declarative alternative: give the anchor class "form-toggle", which module 128's
	 * selector excludes and which has no CSS anywhere. Never use "smooth" — that handler
	 * does querySelector(href) and throws on "/cart".)
	 */
	document.addEventListener(
		'click',
		function (event) {
			var trigger = closest(event.target, '[data-cart-drawer-open]');
			if (!trigger) return;

			event.preventDefault();
			event.stopPropagation();

			if (isOpen) {
				close();
				return;
			}

			// Refresh before opening: the cart may have changed in another tab.
			window
				.fetch('/?sections=cart-drawer', { cache: 'no-store' })
				.then(function (res) {
					return res.json();
				})
				.then(function (payload) {
					renderSection(payload);
					open(trigger);
				})
				.catch(function () {
					// Show what we already have rather than navigating away.
					open(trigger);
				});
		},
		true
	);

	/* --------------------------------------------------------- drawer internals */

	drawer.addEventListener('click', function (event) {
		if (closest(event.target, '[data-cart-drawer-close]')) {
			event.preventDefault();
			close();
			return;
		}

		var step = closest(event.target, '[data-cart-quantity]');
		if (step) {
			event.preventDefault();
			var key = step.getAttribute('data-cart-line-key');
			var field = drawer.querySelector('[data-cart-quantity-input][data-cart-line-key="' + key + '"]');
			var current = field ? parseInt(field.value, 10) || 0 : 0;
			var next = step.getAttribute('data-cart-quantity') === 'plus' ? current + 1 : current - 1;
			var dir = step.getAttribute('data-cart-quantity');
			changeLine(key, Math.max(0, next), '[data-cart-quantity="' + dir + '"][data-cart-line-key="' + key + '"]');
			return;
		}

		var remove = closest(event.target, '[data-cart-remove]');
		if (remove) {
			event.preventDefault();
			// No hint: the line is about to disappear, so refocus falls back to Close.
			changeLine(remove.getAttribute('data-cart-line-key'), 0);
		}
	});

	drawer.addEventListener('change', function (event) {
		var field = closest(event.target, '[data-cart-quantity-input]');
		if (field) {
			var fieldKey = field.getAttribute('data-cart-line-key');
			var value = parseInt(field.value, 10);
			changeLine(
				fieldKey,
				isNaN(value) || value < 0 ? 0 : value,
				'[data-cart-quantity-input][data-cart-line-key="' + fieldKey + '"]'
			);
			return;
		}

		var note = closest(event.target, '[data-cart-note]');
		if (note) saveNote(note);
	});

	drawer.addEventListener('keydown', function (event) {
		if (event.key === 'Escape') {
			event.preventDefault();
			close();
			return;
		}

		// The checkout button is the form's only submit control, so pressing Enter in a
		// quantity field implicitly submits to /cart and drops the customer out of the
		// drawer. event.submitter cannot tell that apart from a real checkout click, so
		// stop it at the field and commit the quantity instead.
		if (event.key === 'Enter') {
			var field = closest(event.target, '[data-cart-quantity-input]');
			if (field) {
				event.preventDefault();
				var enterKey = field.getAttribute('data-cart-line-key');
				var enterValue = parseInt(field.value, 10);
				changeLine(
					enterKey,
					isNaN(enterValue) || enterValue < 0 ? 0 : enterValue,
					'[data-cart-quantity-input][data-cart-line-key="' + enterKey + '"]'
				);
				return;
			}
		}

		trapFocus(event);
	});

	function changeLine(key, quantity, focusHint) {
		if (!key) return;
		setError('');

		mutate('/cart/change.js', { id: key, quantity: quantity })
			.then(function (payload) {
				renderSection(payload);
				refocusAfterSwap(focusHint);
				if (quantity === 0) {
					announce(strings.removed || '');
				} else if (payload && typeof payload.item_count === 'number') {
					var unit = payload.item_count === 1 ? strings.itemsOne : strings.itemsOther;
					announce(payload.item_count + ' ' + (unit || ''));
				}
			})
			.catch(function (err) {
				if (err && err.handled) {
					setError(err.message);
					announce(err.message);
				} else {
					bail();
				}
			});
	}

	/**
	 * Note saves deliberately do NOT request sections — swapping the DOM out from under a
	 * textarea destroys the caret and any un-blurred text.
	 */
	function saveNote(field) {
		var wrapper = field.closest('.cart-drawer__note');
		request('/cart/update.js', { note: field.value })
			.then(function () {
				if (wrapper) {
					wrapper.classList.add('is-saved');
					window.setTimeout(function () {
						wrapper.classList.remove('is-saved');
					}, 2000);
				}
				announce(strings.noteSaved || '');
			})
			.catch(function () {
				setError(strings.error || '');
			});
	}

	/**
	 * Restoring from the back/forward cache replays a stale DOM, so the cart may have moved
	 * on — in this tab's history or in another tab. Re-render from the server, but never
	 * open the drawer: the customer did not ask for it.
	 */
	window.addEventListener('pageshow', function (event) {
		if (!event.persisted) return;

		window
			.fetch('/?sections=cart-drawer', { cache: 'no-store' })
			.then(function (res) {
				return res.json();
			})
			.then(function (payload) {
				var wasOpen = isOpen;
				renderSection(payload);
				if (!wasOpen) {
					drawer.classList.remove('is-open');
					drawer.setAttribute('hidden', '');
					drawer.setAttribute('aria-hidden', 'true');
				}
			})
			.catch(function () {
				/* stale counts are not worth a navigation */
			});
	});

	/* ------------------------------------------------------------------- startup */

	applyDiscounts();
})();
